import React, { useCallback, useMemo } from 'react';
import { showConfirm } from '@/utils/common/alert';
import { StackScreenProps } from '@react-navigation/stack';

import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { DESKTOP_CONFIRM_COPY } from '@/constants/admin';
import { useOrganizationForm } from '@/hooks/admin/useOrganizationForm';
import {
  OrganizationInfoSection,
  PaymentAccountsSection,
} from '@/components/admin/organizations/form-sections';
import { MobileFormScreen } from '@/components/common/admin/mobile/MobileFormScreen';

type CreateOrganizationScreenProps = StackScreenProps<AdminStackParamList, 'CreateOrganization'>;

export const CreateOrganizationScreen: React.FC<CreateOrganizationScreenProps> = ({
  navigation,
}) => {
  const {
    formData,
    errors,
    isLoading,
    updateField,
    updatePaymentAccount,
    createOrganization,
  } = useOrganizationForm({ organization: null, onClose: () => navigation.goBack() });

  const isSubmitting = isLoading;

  // Double-confirm discard on cancel
  const confirmDiscardTwice = useCallback(async (onConfirm: () => void) => {
    const confirmed = await showConfirm(
      DESKTOP_CONFIRM_COPY.discardTitle,
      DESKTOP_CONFIRM_COPY.discardMessage,
      DESKTOP_CONFIRM_COPY.discardConfirmText,
      DESKTOP_CONFIRM_COPY.discardCancelText
    );

    if (confirmed) {
      onConfirm();
    }
  }, []);

  const handleCancel = useCallback(() => {
    confirmDiscardTwice(() => navigation.goBack());
  }, [confirmDiscardTwice, navigation]);

  // Memoized scroll content to prevent unnecessary re-renders
  const scrollContent = useMemo(() => (
    <>
      <OrganizationInfoSection
        name={formData.name}
        domain={formData.domain}
        nameError={errors.name}
        domainError={errors.domain}
        onNameChange={(text) => updateField('name', text)}
        onDomainChange={(text) => updateField('domain', text.toLowerCase())}
        disabled={isLoading}
      />

      <PaymentAccountsSection
        paymentAccounts={formData.paymentAccounts}
        onPaymentAccountChange={updatePaymentAccount}
        disabled={isLoading}
      />
    </>
  ), [
    formData,
    errors,
    isLoading,
    updateField,
    updatePaymentAccount,
  ]);

  return (
    <MobileFormScreen
      screenName="CreateOrganization"
      navigation={navigation}
      isSubmitting={isSubmitting}
      submitText="Create"
      onSubmit={createOrganization}
      onCancel={handleCancel}
    >
      {scrollContent}
    </MobileFormScreen>
  );
};
